import React from "react";
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  Platform,
} from "react-native";
import Svg, { Path } from "react-native-svg";
import DLOTooltip from "./DLOTooltip";
import FontFamily from "../../../constants/fonts";
import { useTheme } from "../../../theme";

export interface DLOBreadcrumbItem {
  id: string;
  label: string;
  href?: string;
  onPress?: () => void;
  isCurrentPage?: boolean;
}

export interface DLOBreadcrumbProps {
  items: DLOBreadcrumbItem[];
  showHomeIcon?: boolean;
  onHomePress?: () => void;
  homeTooltip?: string;
  maxItems?: number;
  style?: any;
}

// Home Icon from Figma
const HomeIcon = ({ color = "#005CBC" }) => (
  <Svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <Path
      d="M10 4.74167L14.1667 8.49167V15H12.5V10H7.50001V15H5.83334V8.49167L10 4.74167ZM10 2.5L1.66667 10H4.16667V16.6667H9.16667V11.6667H10.8333V16.6667H15.8333V10H18.3333L10 2.5Z"
      fill={color}
    />
  </Svg>
);

// Chevron Right Icon from Figma
const ChevronRightIcon = ({ color }: { color?: string } = {}) => (
  <Svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <Path
      d="M4.61751 3.87988L8.68862 7.95988L4.61751 12.0399L5.87084 13.2932L11.2042 7.95988L5.87084 2.62655L4.61751 3.87988Z"
      fill={color}
    />
  </Svg>
);

const DLOBreadcrumb: React.FC<DLOBreadcrumbProps> = ({
  items,
  showHomeIcon = true,
  onHomePress,
  homeTooltip = "Home",
  maxItems = 6,
  style,
}) => {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  const currentPageColor = isDark ? '#FFFFFF' : '#616161';
  // Truncate items if necessary
  const displayItems =
    items.length > maxItems
      ? [
          ...items.slice(0, 1),
          { id: "ellipsis", label: "...", isEllipsis: true },
          ...items.slice(-(maxItems - 2)),
        ]
      : items;

  const handleItemPress = (item: DLOBreadcrumbItem) => {
    if (item.isCurrentPage) return;

    if (item.onPress) {
      item.onPress();
    } else if (item.href && Platform.OS === "web") {
      // Handle navigation for web
      window.location.href = item.href;
    }
  };

  const renderBreadcrumbItem = (item: DLOBreadcrumbItem, index: number) => {
    const isLast = index === displayItems.length - 1;
    const isEllipsis = (item as any).isEllipsis;

    return (
      <View key={item.id} style={styles.itemContainer}>
        {/* Breadcrumb Item */}
        {isEllipsis ? (
          <Text style={[styles.ellipsisText, { color: currentPageColor }]}>...</Text>
        ) : (
          <TouchableOpacity
            style={styles.breadcrumbItem}
            onPress={() => handleItemPress(item)}
            disabled={item.isCurrentPage}
            activeOpacity={item.isCurrentPage ? 1 : 0.7}
          >
            <Text
              style={[
                styles.breadcrumbText,
                item.isCurrentPage && [styles.currentPageText, { color: currentPageColor }],
              ]}
              numberOfLines={1}
            >
              {item.label}
            </Text>
          </TouchableOpacity>
        )}

        {/* Separator */}
        {!isLast && (
          <View style={styles.separator}>
            <ChevronRightIcon color={currentPageColor} />
          </View>
        )}
      </View>
    );
  };

  return (
    <View style={[styles.container, style]}>
      {/* Home Icon */}
      {showHomeIcon && (
        <>
          <DLOTooltip content={homeTooltip} position="top" trigger="hover">
            <TouchableOpacity
              style={styles.homeButton}
              onPress={onHomePress}
              activeOpacity={0.7}
            >
              <HomeIcon />
            </TouchableOpacity>
          </DLOTooltip>

          {displayItems.length > 0 && (
            <View style={styles.separator}>
              <ChevronRightIcon color={currentPageColor} />
            </View>
          )}
        </>
      )}

      {/* Breadcrumb Items */}
      {displayItems.map(renderBreadcrumbItem)}
    </View>
  );
};

// Convenience component for common usage patterns
export const DLOSimpleBreadcrumb: React.FC<{
  path: string[];
  onNavigate?: (index: number, path: string) => void;
  onHomePress?: () => void;
  style?: any;
}> = ({ path, onNavigate, onHomePress, style }) => {
  const items: DLOBreadcrumbItem[] = path.map((segment, index) => ({
    id: `breadcrumb-${index}`,
    label: segment,
    isCurrentPage: index === path.length - 1,
    onPress: () => onNavigate?.(index, segment),
  }));

  return (
    <DLOBreadcrumb items={items} onHomePress={onHomePress} style={style} />
  );
};

// Labor Optimization specific breadcrumb matching Figma design
export const DLOLaborOptimizationBreadcrumb: React.FC<{
  currentPage?: string;
  periodWeekYear?: string;
  onHomePress?: () => void;
  onLaborOptimizationPress?: () => void;
  style?: any;
}> = ({
  currentPage = "Location Settings",
  periodWeekYear,
  onHomePress,
  onLaborOptimizationPress,
  style,
}) => {
  const items: DLOBreadcrumbItem[] = [
    {
      id: "labor-optimization",
      label: "Labor Optimization",
      onPress: onLaborOptimizationPress,
    },
    {
      id: "current-page",
      label: currentPage,
      onPress: undefined,
    },
  ];

  // Add period/week/year as the last breadcrumb item if provided
  if (periodWeekYear) {
    items.push({
      id: "period-week-year",
      label: periodWeekYear,
      isCurrentPage: true,
    });
  } else {
    // Mark the current page as current if no period/week/year
    items[items.length - 1].isCurrentPage = true;
  }

  return (
    <DLOBreadcrumb items={items} onHomePress={onHomePress} style={style} />
  );
};

const styles = StyleSheet.create({
  container: {
    flexDirection: "row",
    alignItems: "center",
    height: 24,
    gap: 4,
    flexWrap: "wrap",
  },
  homeButton: {
    padding: 2, // Small padding for better touch target
    borderRadius: 4,
  },
  itemContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  breadcrumbItem: {
    paddingVertical: 2,
    paddingHorizontal: 4,
    borderRadius: 4,
    minHeight: 20,
    justifyContent: "center",
  },
  breadcrumbText: {
    color: "#005CBC",
    fontFamily: FontFamily.regular,
    fontSize: 14,
    fontWeight: "400",
    lineHeight: 20,
  },
  currentPageText: {
    color: "#616161",
  },
  ellipsisText: {
    color: "#616161",
    fontFamily: FontFamily.regular,
    fontSize: 14,
    fontWeight: "400",
    lineHeight: 20,
    paddingHorizontal: 4,
  },
  separator: {
    flexShrink: 0,
  },
});

export default DLOBreadcrumb;
